import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { C } from '../../theme.js';
import { Eyebrow } from '../../components/Primitives.jsx';

const MONO = "'JetBrains Mono', ui-monospace, monospace";

function Blank() {
  return <span style={{ color: C.fg6, fontStyle: 'italic' }}>(blank)</span>;
}

function Cell({ v, bold }) {
  return (
    <td style={{
      padding: '3px 8px', fontFamily: MONO, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis',
      whiteSpace: 'nowrap', color: bold ? C.fg1 : C.fg3, fontWeight: bold ? 700 : 400,
    }} title={v ?? ''}>{v == null ? <Blank /> : v}</td>
  );
}

// Live preview for a value_body answer (model from mapValueBodyResponse).
export function ValueBodyPreview({ model, rowsNote }) {
  if (!model) return null;
  return (
    <>
      <div style={{ color: C.fg2, fontSize: '.84rem', fontWeight: 600 }}>{model.headline}</div>
      {(model.truncatedNote || rowsNote) && (
        <div style={{ color: C.fg5, fontSize: '.72rem', marginTop: 2 }}>
          {[model.truncatedNote, rowsNote].filter(Boolean).join(' · ')}
        </div>
      )}

      {model.problems.map((p) => (
        <div key={p.key} style={{ background: 'var(--warn-card-bg)', border: '1px solid var(--warn-border)', color: C.warn, borderRadius: 8, padding: '8px 10px', fontSize: '.78rem', marginTop: 8 }}>
          <div style={{ fontWeight: 700 }}>{p.title}: {p.count} row{p.count === 1 ? '' : 's'} ({p.hint})</div>
          {p.examples.map((e) => (
            <div key={e.row} style={{ fontFamily: MONO, fontSize: '.74rem', marginTop: 2 }}>row {e.row}: {e.message}</div>
          ))}
        </div>
      ))}

      {model.staticWarnings.map((w, i) => (
        <div key={i} style={{ marginTop: 8, color: C.warn, fontSize: '.78rem', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{w.message}{w.where ? ` (${w.where})` : ''}</span>
        </div>
      ))}

      {model.table.length > 0 && (
        <div style={{ marginTop: 8, border: `1px solid ${C.border}`, borderRadius: 8, maxHeight: 260, overflow: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '.76rem' }}>
            <thead>
              <tr style={{ position: 'sticky', top: 0, background: C.bar }}>
                <th style={{ textAlign: 'right', padding: '4px 8px', color: C.fg5, fontWeight: 600 }}>#</th>
                {model.refCols.map(c => <th key={`r${c}`} style={{ textAlign: 'left', padding: '4px 8px', color: C.fg4, fontFamily: MONO, fontWeight: 600 }}>{c}</th>)}
                {model.outputs.map(o => <th key={`o${o}`} style={{ textAlign: 'left', padding: '4px 8px', color: C.accentBright, fontFamily: MONO, fontWeight: 700, borderLeft: `1px solid ${C.border}` }}>{'\u2192 '}{o}</th>)}
              </tr>
            </thead>
            <tbody>
              {model.table.map((r) => (
                <tr key={r.row} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={{ textAlign: 'right', padding: '3px 8px', color: r.flagged ? C.warn : C.fg5 }}>{r.row}{r.flagged ? ' !' : ''}</td>
                  {r.refs.map((v, i) => <Cell key={`r${i}`} v={v} />)}
                  {r.values.map((v, i) => <Cell key={`o${i}`} v={v} bold />)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {model.listNote && <div style={{ color: C.fg5, fontSize: '.7rem', marginTop: 4 }}>{model.listNote}</div>}

      <Eyebrow style={{ marginTop: 12, marginBottom: 6 }}>Values per output</Eyebrow>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {model.perOutput.map((o) => (
          <div key={o.name} style={{ background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 10px', fontSize: '.76rem' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
              <span style={{ fontFamily: MONO, color: C.accentBright, fontWeight: 700 }}>{o.name}</span>
              <span style={{ color: o.blank ? C.warn : C.fg4 }}>{o.blank} blank</span>
            </div>
            {o.top.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                {o.top.map((t, i) => (
                  <span key={i} style={{ fontFamily: MONO, color: C.fg3, background: C.app, border: `1px solid ${C.border}`, borderRadius: 10, padding: '0 7px' }}>
                    {String(t.value)} <span style={{ color: C.fg5 }}>×{t.count}</span>
                  </span>
                ))}
              </div>
            ) : (
              <div style={{ color: C.fg5, marginTop: 2 }}>No non-blank values.</div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
